// INFANT — 4–17 months
// The deepest guide: patterns, independent sleep, sleep training questions.
// Educational, non-medical, no outcome promises.

export const infant = {
  id: "4-11",
  num: "03",
  unit: "4\u201317 months",
  name: "Infant",
  img: "https://images.pexels.com/photos/35119991/pexels-photo-35119991.jpeg?auto=compress&cs=tinysrgb&w=900",
  alt: "A parent holding their sleeping baby in a quiet room",
  titleLines: [
    { t: "Maybe your baby" },
    { t: "can sleep." },
    { t: "They just can\u2019t seem", em: true },
    { t: "to do it consistently.", em: true },
  ],
  resourceTitle: "Infant Sleep Guide",
  resourceDescriptor: "Make sense of the night waking, the short naps and the bedtime that takes forever — and start to see what’s actually driving them.",
  introFragments: [
    "They sleep beautifully in your arms and wake when they touch the crib.",
    "Every nap ends after 32 minutes.",
    "They fall asleep with a bottle and seem to need the same thing again when they wake.",
    "Bedtime works Tuesday. Wednesday feels like you\u2019ve never put this baby to sleep before.",
  ],
  introAfter: "Maybe they were sleeping well. Then something changed.",
  introTurn: {
    lead: "This is often the stage when the question changes from:",
    from: "When will my baby sleep?",
    to: "What am I supposed to change?",
  },
  topics: [
    {
      id: "waking-every-two-hours",
      q: "Why is my baby waking every two hours?",
      body: [
        "First, reframe the question. \u2018Waking every two hours\u2019 describes the symptom — it doesn\u2019t name the cause. Babies cycle through lighter sleep all night long; the question is what happens when they surface.",
        "That\u2019s why the whole sleep picture matters more than the timestamp. How your baby falls asleep initially, what bedtime looks like, what naps are doing, how feeding fits in, what developmental changes are underway, what the room is like, and what happens when you respond — each piece shapes the others.",
        "Two babies can wake at identical times for completely different reasons. One needs the exact conditions they fell asleep with recreated at every waking. Another is undertired from a day of long naps. A third is mid-leap in development. The waking looks the same on the monitor; the situations aren\u2019t.",
      ],
      observe: [
        "How does bedtime begin — and how does your baby actually fall asleep?",
        "How long until the first waking?",
        "What happens at each waking — and what does your baby need to return to sleep?",
        "Is the pattern similar every night?",
        "What are naps doing during the day?",
      ],
    },
    {
      id: "short-naps",
      q: "Why are my baby\u2019s naps only 30–45 minutes?",
      body: [
        "The scene is universal. You put them down. You finally sit down. You look at the monitor. Thirty-two minutes. Eyes open. Again.",
        "What you\u2019re seeing is the architecture of infant sleep. Babies sleep in cycles, with a lighter phase at the end of each one — and at this age a cycle runs roughly 30–45 minutes. A baby who wakes at that mark has completed a cycle. The skill of linking one cycle into the next develops over time, and it develops faster for some babies than others.",
        "The trap is treating every short nap as an isolated defect to fix. A short nap means more in context: your baby\u2019s age, when the nap started, how they fell asleep, what the rest of the day\u2019s sleep looks like, and what happens after the wake-up. Sometimes the pattern asks for attention. Sometimes it\u2019s just a baby being on schedule with their own development.",
      ],
      observe: [
        "Are the short naps consistent, or occasional?",
        "What time of day do they happen?",
        "How did your baby fall asleep at the start of the nap?",
        "What\u2019s their mood after waking — refreshed or still tired?",
      ],
    },
    {
      id: "crib-transfer",
      q: "Why does my baby wake during the crib transfer?",
      body: [
        "Rock. Wait. Lower. Freeze. Hand on chest. Slowly remove hand. Take one step backward. Eyes open. Start over.",
        "The transfer fails because it isn\u2019t really one event — it\u2019s a complete change of sleep conditions. Your baby drifted off with warmth, motion and contact. The crib offers stillness and space. Babies who surface easily notice the difference immediately.",
        "The valuable insight isn\u2019t that transfers are hard — it\u2019s that how sleep begins can matter at every later waking, not just at bedtime. That doesn\u2019t mean how your baby falls asleep is always the cause of every wake-up. It means it\u2019s one of the first things worth understanding about your child\u2019s particular sleep.",
      ],
    },
    {
      id: "feeding-to-sleep",
      q: "Is feeding my baby to sleep a problem?",
      body: [
        "Feeding your baby to sleep is not a moral failure. Rocking your baby to sleep isn\u2019t either. These are some of the oldest, most natural caregiving tools that exist, and for many families they work beautifully for a long time.",
        "The honest distinction is between \u2018it works for us\u2019 and \u2018I have to do this every single time and I don\u2019t want to anymore.\u2019 When a repeated condition — the bottle, the breast, the rocking — is required at bedtime and again at every night waking, some families reach a point where the tool has become the problem they\u2019re managing.",
        "If you\u2019re there, it doesn\u2019t mean you did anything wrong. It means your baby has organized their sleep around a condition you\u2019d like to change — and that\u2019s something that can be worked with thoughtfully, at a pace your family is actually comfortable with.",
      ],
    },
    {
      id: "sleep-associations",
      q: "What is a sleep association, actually?",
      body: [
        "In parent language: it\u2019s whatever your baby has learned to expect around the moment of falling asleep. The conditions. The cues. The bottle, the motion, the song, the dark room, your hand on their chest.",
        "Associations aren\u2019t inherently bad — some are just predictable cues that make sleep easier, and every good bedtime routine is built on them. The conversation changes when a cue becomes a dependency: when your baby can\u2019t fall asleep, or return to sleep, without a specific thing that requires you, every time, all night.",
        "The question was never \u2018does my baby have sleep associations\u2019 — every baby does. It\u2019s whether the ones your family has are ones you want to keep.",
      ],
    },
    {
      id: "independent-sleep",
      q: "What does \u2018independent sleep\u2019 actually mean?",
      body: [
        "Not emotional separation, and not a baby left to figure out the world alone. Independent sleep simply describes a child who can fall asleep — and increasingly, return to sleep between cycles — without needing the exact same parental intervention every single time.",
        "It exists on a spectrum. A baby who falls asleep in the crib with you beside it is more independent than one who needs to be rocked for forty minutes, and less independent than one who babbles themselves off after the routine. Most families are moving along that spectrum, not jumping to an endpoint.",
        "What it looks like, how quickly, and whether it\u2019s even a goal right now — those are family decisions. The term is a description, not a commandment.",
      ],
    },
    {
      id: "wake-windows",
      q: "What are wake windows — and how much do they matter?",
      body: [
        "A wake window is the stretch of time your baby can comfortably be awake before needing sleep again. The concept is genuinely useful: a baby put down too early lies there frustrated; too late, and overtiredness makes everything harder.",
        "But here\u2019s the honest second half. Wake windows can become the thing you stare at all day, wondering whether eleven minutes ruined bedtime. They\u2019re a range, not a countdown — and they\u2019re one piece of a larger picture that includes your baby\u2019s sleepy cues, the quality of the last nap, their age, and the rhythm of the specific day you\u2019re having.",
        "Use them as a map, not a law. Your baby\u2019s behavior is the territory.",
      ],
    },
    {
      id: "is-schedule-the-problem",
      q: "Is my baby\u2019s schedule the problem?",
      body: [
        "Maybe — but a schedule can only tell you so much. Nap timing, nap duration, bedtime, morning wake time and feeding all interact, and a schedule is a summary of a pattern, not an explanation of it.",
        "The most common schedule mistake isn\u2019t the schedule itself — it\u2019s changing it after one difficult day. A single bad nap day proves nothing. A pattern across a week is information.",
        "Before moving anything, watch. When do the hard moments cluster? What happens on the days that go well? The schedule that emerges from your baby\u2019s actual behavior will always beat the one copied from a chart.",
      ],
    },
    {
      id: "bedtime-false-start",
      q: "What is a bedtime false start?",
      body: [
        "It\u2019s the term parents use when the baby falls asleep at bedtime — and then wakes again surprisingly soon after, sometimes twenty or forty minutes in, as if the night never quite started.",
        "One false start is just a night. A repeating pattern of them deserves a wider lens: what the whole day looked like, when the last nap ended, how bedtime went, and what the waking needs. Because the false start sits at the junction of the day\u2019s sleep and the night\u2019s, it often reflects something about both.",
        "There isn\u2019t a universal cause — which is exactly why the pattern around it matters more than the event itself.",
      ],
    },
    {
      id: "sleep-regression",
      q: "Is this a sleep regression?",
      body: [
        "Parents usually reach for \u2018regression\u2019 when a baby who was sleeping predictably suddenly isn\u2019t — often around a developmental leap, a new skill, or a transition. The word describes the experience accurately: sleep went backwards.",
        "But the label alone doesn\u2019t tell you what to do, and that\u2019s where families get stuck. \u2018It\u2019s a regression\u2019 can become a reason to wait passively for weeks, or to change five things at once in a panic.",
        "The better questions: what changed, and when? What does bedtime look like now? What happened to naps? What does your baby need at each waking that they didn\u2019t need before? The answers tell you whether you\u2019re watching a passing wave or a shifted pattern — and those ask for different responses.",
      ],
    },
    {
      id: "early-waking",
      q: "Why is my baby waking so early?",
      body: [
        "Early waking is one of the most stubborn sleep patterns, partly because the early morning hours hold the lightest sleep of the night. By 4 or 5 a.m., the pressure to sleep has largely discharged, and anything mildly uncomfortable — light, noise, hunger, habit — can end the night.",
        "Which is why early waking rarely responds to a single tweak. Bedtime timing, nap distribution, the room\u2019s darkness, the established pattern of what happens at that waking, and plain individual variation all sit in the mix.",
        "What doesn\u2019t help: expecting a particular wake time by force of will. What does help: reading the whole 24-hour picture before adjusting any one part of it.",
      ],
    },
    {
      id: "contact-naps",
      q: "What about contact naps at this age?",
      body: [
        "Same rule as always: no shame. \u2018I love this and it works for us\u2019 is a complete sentence. Plenty of families keep contact naps deep into the first year and beyond, and if that\u2019s you, there is nothing here to fix.",
        "The other version sounds like: \u2018this is the only way my baby can nap, and I need another option.\u2019 A baby who has only ever napped in arms has simply never practiced napping any other way — that\u2019s a skills-and-conditions question, not a discipline one.",
        "Which conversation you\u2019re having depends entirely on what you want. The parent\u2019s goal is the starting point, not the internet\u2019s opinion.",
      ],
    },
    {
      id: "thinking-about-sleep-training",
      q: "When do parents start thinking about sleep training?",
      body: [
        "Usually it isn\u2019t because a parent wakes up excited to \u2018sleep train.\u2019 It\u2019s because something they do at every nap, every bedtime or every waking has quietly stopped being sustainable.",
        "It sounds like: I can\u2019t rock for 45 minutes every time. They wake every time I put them down. They need the bottle at every waking. We tried sleep training for three nights and stopped because we weren\u2019t sure what we were doing. Every person online says something different.",
        "That moment — exhausted, uncertain, mid-contradiction — is actually a better place to start than \u2018which method is best?\u2019 The useful questions come first: What is happening now? What do you want to change? What are you comfortable with? What does your child\u2019s whole sleep picture look like? Methods come after answers.",
      ],
    },
    {
      id: "sleep-training-not-working",
      q: "Why does sleep training sometimes feel like it isn\u2019t working?",
      body: [
        "Usually not because the family failed. When a sleep approach stalls, the realistic suspects are structural: the goal was never clearly defined, the approach changed every few nights, the timing or schedule underneath was off, the expectations didn\u2019t match the baby\u2019s development, or the method on paper was never one the parents could actually carry out at 3 a.m.",
        "That last one is underrated. An approach only works if the people implementing it believe in it enough to be consistent — a plan that makes a parent miserable rarely survives contact with a hard night.",
        "None of this is a diagnosis of your situation, and no approach comes with guarantees. It\u2019s a reminder that \u2018it isn\u2019t working\u2019 is the start of an investigation, not a verdict.",
      ],
    },
    {
      id: "what-to-track",
      q: "What should I track before changing everything?",
      body: [
        "Before touching anything, gather a few days of honest notes. Morning wake time. When naps happened and how long they lasted. Bedtime. How your baby fell asleep. When the first waking came, what it needed, and what happened after. Feeding where it\u2019s relevant. Anything unusual about the day.",
        "You aren\u2019t trying to become a sleep scientist. You\u2019re looking for a pattern — and patterns only show themselves across days, never across one bad night.",
        "Parents who arrive at a conversation with even rough notes are hours ahead, because the questions stop being abstract. \u2018She wakes a lot\u2019 becomes \u2018she wakes forty minutes after bedtime, then hourly after three.\u2019 That second sentence is where real progress starts.",
      ],
    },
    {
      id: "pediatrician",
      q: "When should I talk to my pediatrician?",
      body: [
        "Sleep consulting supports behavior, routine and rhythm — it doesn\u2019t replace medical evaluation. Concerns involving breathing during sleep, feeding, growth, illness, unusual symptoms or development belong with your child\u2019s healthcare professional.",
        "If your gut says a waking pattern is more than behavioral, trust it and make the call. Once health questions are settled, the sleep picture becomes much clearer to work on.",
      ],
    },
  ],
  checklist: {
    title: ["My baby…"],
    items: [
      "wakes frequently overnight",
      "takes 30–45 minute naps",
      "wakes during crib transfers",
      "feeds to sleep",
      "rocks or bounces to sleep",
      "needs the same help after each waking",
      "suddenly stopped sleeping well",
      "has unpredictable bedtimes",
      "wakes very early",
      "struggles to fall asleep independently",
      "mostly contact naps",
      "has us constantly changing the schedule",
      "has us considering sleep training",
      "already tried sleep training and we\u2019re stuck",
    ],
    after: [
      { t: "The more boxes you check," },
      { t: "the less likely this is about" },
      { t: "one random thing.", em: true },
      { t: "This is where", em: true },
      { t: "the details matter.", em: true },
    ],
  },
  ending: {
    lines: [
      { t: "You\u2019ve probably" },
      { t: "read enough about" },
      { t: "baby sleep." },
      { t: "Now let\u2019s look", em: true },
      { t: "at your baby.", em: true },
    ],
    sub: null,
  },
  deepLink: { href: "/baby-sleep-consultant", label: "Explore baby & infant sleep support" },
  ctas: [
    { type: "book", label: "Book my complimentary sleep assessment" },
    { type: "tell", label: "Tell Andrea what\u2019s going on" },
    { type: "text", label: "Text Andrea" },
  ],
};
